<script setup>
import { all } from "@awesome.me/kit-698d8619ec/icons";
import { ChevronUpDownIcon } from "@heroicons/vue/20/solid";
import { computed, ref } from "vue";

import { Dialog, DialogOverlay, DialogPanel } from "./ui/dialog";

const props = defineProps({
  context: {
    type: Object,
    required: true,
  },
});

const isOpen = ref(false);

const search = ref("");

function handleSelectOption(option) {
  props.context.node.input(option);
  isOpen.value = false;
}

const options = computed(() => {
  const prefixMap = new Map([
    ["fab", "fa-brands"],
    ["fass", "fa-sharp fa-solid"],
    ["fak", "fa-kit"],
  ]);
  return [...new Set(all.map((i) => `${prefixMap.get(i.prefix)} fa-${i.iconName}`))];
});

const searchAliases = new Map([
  ["admin", ["user-crown", "user-tie"]],
  ["amazon", ["aws"]],
  ["azure", ["microsoft"]],
  ["firewall", ["black-brick-fire"]],
  ["gcp", ["google"]],
  ["shell", ["terminal"]],
  ["ssh", ["code", "lock", "key", "server", "terminal"]],
  ["vpn", ["shield"]],
]);

const filteredOptions = computed(() => {
  return !search.value
    ? options.value
    : options.value.filter((option) => {
        const aliases = searchAliases.get(search.value.toLowerCase());
        if (aliases) {
          return (
            option.toLowerCase().includes(search.value.toLowerCase()) ||
            aliases.some((alias) => option.toLowerCase().includes(alias.toLowerCase()))
          );
        }
        return option.toLowerCase().includes(search.value.toLowerCase());
      });
});

function iconDisplayName(name) {
  const fasplits = name.split("fa-");
  return fasplits[fasplits.length - 1].replaceAll("--", "-");
}
</script>

<template>
  <button
    :id="props.context.id"
    class="formkit-input flex h-[42px] min-w-0 grow appearance-none items-center justify-between text-ellipsis border-none bg-transparent bg-none px-3 text-base text-neutral-700 outline-none [color-scheme:light] selection:bg-blue-100 selection:text-neutral-700 focus:ring-0 data-[placeholder]:text-neutral-400 group-data-[disabled]:!cursor-not-allowed group-data-[multiple]:!p-0 group-data-[prefix-icon]:!pl-0 group-data-[suffix-icon]:!pr-0 group-data-[has-overlay]:selection:!text-transparent dark:text-neutral-300 dark:[color-scheme:dark] dark:data-[placeholder]:text-neutral-400/50"
    type="button"
    @click="isOpen = !isOpen"
    :aria-label="`${iconDisplayName(props.context.node.value)}`"
  >
    <font-awesome-icon :icon="props.context._value" fixed-width />
    <ChevronUpDownIcon class="h-4 w-4 text-muted-foreground" />
  </button>

  <Dialog :show="isOpen" @close="isOpen = false">
    <DialogOverlay @click="isOpen = false" />

    <DialogPanel theme="rose" class="w-full !max-w-[640px]">
      <div>
        <div
          class="formkit-inner flex w-full items-center rounded border border-neutral-400 bg-white px-3 py-2 text-base shadow focus-within:!border-blue-500 focus-within:ring-1 focus-within:!ring-blue-500 group-[]/multistep:shadow-none group-[]/repeater:shadow-none group-data-[disabled]:!cursor-not-allowed group-data-[invalid]:border-red-500 group-data-[disabled]:bg-neutral-100 group-data-[invalid]:ring-1 group-data-[invalid]:ring-red-500 dark:border-neutral-500 dark:bg-transparent dark:group-data-[invalid]:border-red-500 dark:group-data-[disabled]:bg-neutral-800/5 dark:group-data-[invalid]:ring-red-500"
        >
          <input
            v-model="search"
            class="formkit-input min-h-[1.5em] min-w-0 grow appearance-none border-none bg-transparent p-0 text-base text-neutral-700 outline-none [color-scheme:light] selection:bg-blue-100 selection:text-neutral-700 placeholder:text-neutral-400 focus:ring-0 group-data-[disabled]:!cursor-not-allowed group-data-[has-overlay]:selection:!text-transparent dark:text-neutral-300 dark:placeholder-neutral-400/50 dark:[color-scheme:dark]"
            placeholder="Search"
          />
        </div>
      </div>

      <div class="grid max-h-[512px] grid-cols-4 gap-y-8 overflow-y-auto">
        <div v-for="option in filteredOptions" :key="option" class="flex justify-center">
          <button
            class="inline-flex h-[120px] w-[120px] flex-col items-center overflow-x-hidden whitespace-nowrap rounded-md px-[12px] pb-[16px] pt-[24px] text-center text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
            @click="handleSelectOption(option)"
          >
            <font-awesome-icon class="h-[40px] w-[40px]" :icon="option" />
            <span class="hyphen-break mt-[18px] text-[12px]">
              {{ iconDisplayName(option) }}
            </span>
          </button>
        </div>
      </div>
    </DialogPanel>
  </Dialog>
</template>
